import React from 'react';

import type { RJSFSchema } from '@rjsf/utils';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';

import RoomDraftForm from './RoomDraftForm';
import type { FlashOfferRoomDraft } from './utils';

type RoomsStepProperty = Pick<App.Property, 'id' | 'name'> & {
  category?: string | null;
  channel_manager?: string | null;
};

interface Props {
  amenityOptions: Array<string>;
  amenityPrefillNote?: string;
  index: number;
  isBusy: boolean;
  onAddAmenityOption: (amenityName: string) => void;
  onRequestRemove: (draftId: string) => void;
  onUpdateRoomDraft: (draftId: string, updates: Partial<FlashOfferRoomDraft>) => void;
  property: RoomsStepProperty;
  roomDraft: FlashOfferRoomDraft;
  roomTypesFormSchema?: RJSFSchema;
}

export default function RoomDraftAccordion({
  amenityOptions,
  amenityPrefillNote,
  index,
  isBusy,
  onAddAmenityOption,
  onRequestRemove,
  onUpdateRoomDraft,
  property,
  roomDraft,
  roomTypesFormSchema,
}: Props) {
  const isExistingRoom = !!roomDraft.existingRoomName;
  const isSelectedForCreate = roomDraft.shouldCreate && !isExistingRoom;
  const isRoomInputDisabled = isBusy || !isSelectedForCreate;

  return (
    // Proposed rooms start collapsed; a blank one the CM just added starts open since it needs filling in.
    <Accordion variant="outlined" defaultExpanded={!roomDraft.name} disableGutters>
      <AccordionSummary
        component="div"
        expandIcon={<ExpandMoreIcon />}
        sx={{
          minHeight: 72,
          '&.Mui-expanded': { minHeight: 72 },
          '& .MuiAccordionSummary-content': { my: 1.5 },
          '& .MuiAccordionSummary-content.Mui-expanded': { my: 1.5 },
        }}
      >
        <Box flexGrow={1}>
          <Typography variant="h6">{roomDraft.name || roomDraft.existingRoomName || `Room ${index + 1}`}</Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {roomDraft.category}
            </Typography>
          </Stack>
        </Box>

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          onClick={(event) => event.stopPropagation()}
          onFocus={(event) => event.stopPropagation()}
          sx={{ ml: 2 }}
        >
          <IconButton
            aria-label={`Remove room ${index + 1}`}
            disabled={isBusy}
            onClick={() => onRequestRemove(roomDraft.draftId)}
          >
            <DeleteOutlineIcon />
          </IconButton>
        </Stack>
      </AccordionSummary>

      <AccordionDetails>
        <Stack spacing={2}>
          {roomTypesFormSchema ? (
            <RoomDraftForm
              amenityOptions={amenityOptions}
              amenityPrefillNote={amenityPrefillNote}
              channelManager={property.channel_manager}
              disabled={isRoomInputDisabled}
              onAddAmenityOption={onAddAmenityOption}
              onChange={(updates) => onUpdateRoomDraft(roomDraft.draftId, updates)}
              propertyCategory={property.category}
              propertyName={property.name}
              roomDraft={roomDraft}
              schema={roomTypesFormSchema}
            />
          ) : (
            <Alert severity="info">Room fields will appear when the schema has loaded.</Alert>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
